import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getStaffSessionToken } from '@/services/staffSession';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle2, Clock, FileText, Send, XCircle } from 'lucide-react';

/**
 * Automation — the reports the on-prem agent produces, and whether they arrived.
 *
 * Deliberately leads with WHETHER A RUN HAPPENED, not with the newest report.
 * Six streams died silently for twelve days in August and the only signal anyone
 * had was a WhatsApp message that never came. A screen that shows the last report
 * and looks healthy would rebuild exactly that blind spot, so a stream that has
 * missed its window is red at the top of the page.
 *
 * Status and timing come straight from Supabase with the anon key (no patient
 * identity in any of it). Report CONTENT is fetched from /api/report/<runId>,
 * which reads with the service role, because those tables refuse the anon key.
 */

type Automation = {
  id: string; key: string; label: string; category: string;
  window_name: string; cron_note: string | null; expected_by: string | null;
  is_enabled: boolean; last_run_at: string | null; last_status: string | null;
};
type Run = {
  id: string; automation_id: string; run_date: string; status: string;
  finished_at: string | null; insight_count: number;
};
type Metric = { run_id: string; key: string; label: string; value: number };
type Delivery = { run_id: string; wa_account: string; chat_label: string | null; format: string; sent_at: string | null; failed_at: string | null };
type Insight = { kind: string; section: string | null; title: string; detail: string | null; owner: string | null };

const sgtToday = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Singapore' }));

/** A run is LATE once its expected_by time has passed with nothing recorded. */
function streamState(a: Automation, runs: Run[]) {
  const today = sgtToday();
  const iso = today.toISOString().slice(0, 10);
  const todays = runs.find(r => r.automation_id === a.id && r.run_date === iso);
  if (todays) return { state: todays.status === 'ok' ? 'ok' : 'failed', run: todays };
  if (!a.is_enabled) return { state: 'off', run: null };
  if (a.expected_by) {
    const [h, m] = a.expected_by.split(':').map(Number);
    const due = new Date(today); due.setHours(h, m, 0, 0);
    if (today > due) return { state: 'late', run: null };
  }
  return { state: 'pending', run: null };
}

const TONE: Record<string, { cls: string; Icon: typeof CheckCircle2; word: string }> = {
  ok:      { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2,   word: 'Delivered' },
  late:    { cls: 'bg-red-50 text-red-700 border-red-200',             Icon: AlertTriangle,  word: 'Missed its window' },
  failed:  { cls: 'bg-red-50 text-red-700 border-red-200',             Icon: XCircle,        word: 'Failed' },
  pending: { cls: 'bg-slate-50 text-slate-600 border-slate-200',       Icon: Clock,          word: 'Not due yet' },
  off:     { cls: 'bg-slate-50 text-slate-400 border-slate-200',       Icon: Clock,          word: 'Switched off' },
};

const KIND_TONE: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800', stale: 'bg-amber-100 text-amber-800',
  system: 'bg-violet-100 text-violet-800', closed: 'bg-emerald-100 text-emerald-800',
  new: 'bg-blue-100 text-blue-800', note: 'bg-slate-100 text-slate-700',
};

const AutomationView: React.FC = () => {
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [tab, setTab] = useState<'report' | 'items'>('report');

  const { data, isLoading } = useQuery({
    queryKey: ['automation-overview'],
    refetchInterval: 60_000,
    queryFn: async () => {
      const since = new Date(Date.now() - 14 * 864e5).toISOString().slice(0, 10);
      const [a, r, m, d] = await Promise.all([
        supabase.from('automations').select('*').order('window_name').order('key'),
        supabase.from('automation_runs').select('*').gte('run_date', since).order('run_date', { ascending: false }),
        supabase.from('report_metrics').select('*'),
        supabase.from('report_deliveries').select('*'),
      ]);
      return {
        automations: (a.data ?? []) as Automation[],
        runs: (r.data ?? []) as Run[],
        metrics: (m.data ?? []) as Metric[],
        deliveries: (d.data ?? []) as Delivery[],
      };
    },
  });

  const { data: report, isLoading: loadingReport } = useQuery({
    queryKey: ['automation-report', openRun],
    enabled: !!openRun,
    queryFn: async () => {
      // The route needs a live staff session (15 Oct 2026); it answers 401 without one.
      const res = await fetch(`/api/report/${openRun}`, {
        headers: { 'x-staff-session': getStaffSessionToken() ?? '' },
      });
      if (!res.ok) throw new Error(`report ${res.status}`);
      return res.json() as Promise<{ markdown: string | null; html: string | null; insights: Insight[]; run: any }>;
    },
  });

  const states = useMemo(() => {
    if (!data) return [];
    return data.automations.map(a => ({ a, ...streamState(a, data.runs) }));
  }, [data]);

  const problems = states.filter(s => s.state === 'late' || s.state === 'failed');

  if (isLoading) return <div className="p-8 text-slate-500">Loading automations…</div>;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {problems.length > 0 && (
        <Card className="border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-900">
                {problems.length} report{problems.length > 1 ? 's have' : ' has'} not arrived today
              </p>
              <p className="text-sm text-red-800 mt-1">
                {problems.map(p => p.a.label).join(', ')} — expected by{' '}
                {problems.map(p => p.a.expected_by?.slice(0, 5)).filter(Boolean).join(', ')}.
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {states.map(({ a, state, run }) => {
          const tone = TONE[state] ?? TONE.pending;
          const dels = data!.deliveries.filter(d => run && d.run_id === run.id);
          const mets = data!.metrics.filter(m => run && m.run_id === run.id);
          return (
            <Card key={a.id} className={`p-4 border ${tone.cls.split(' ').slice(2).join(' ')}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{a.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{a.cron_note}</p>
                </div>
                <Badge className={`${tone.cls} border shrink-0`}>
                  <tone.Icon className="h-3 w-3 mr-1" />{tone.word}
                </Badge>
              </div>

              {mets.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                  {mets.slice(0, 4).map(m => (
                    <div key={m.key} className="text-xs">
                      <span className="font-semibold text-slate-900 tabular-nums">{m.value}</span>{' '}
                      <span className="text-slate-500">{m.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {dels.length > 0 && (
                <div className="mt-3 space-y-1">
                  {dels.map((d, i) => (
                    <p key={i} className="text-xs text-slate-500 flex items-center gap-1">
                      <Send className="h-3 w-3" />
                      {d.chat_label} <span className="text-slate-400">via {d.wa_account}</span>
                    </p>
                  ))}
                </div>
              )}

              {run && (
                <Button variant="outline" size="sm" className="mt-3 w-full"
                        onClick={() => { setTab('report'); setOpenRun(run.id); }}>
                  <FileText className="h-3.5 w-3.5 mr-1.5" />
                  Read report · {run.insight_count} items
                </Button>
              )}
            </Card>
          );
        })}
      </div>

      <Dialog open={!!openRun} onOpenChange={(o) => !o && setOpenRun(null)}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-5 py-3 border-b shrink-0">
            <DialogTitle className="text-base">
              {report?.run?.automations?.label ?? 'Report'}
              {report?.run?.run_date && (
                <span className="ml-2 font-normal text-slate-500">
                  {new Date(report.run.run_date).toLocaleDateString('en-SG',
                    { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore' })}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 pt-3 shrink-0 flex gap-2 border-b">
            {(['report', 'items'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t === 'report' ? 'Report' : `Items (${report?.insights?.length ?? 0})`}
              </button>
            ))}
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {loadingReport && <p className="p-6 text-slate-500">Loading report…</p>}

            {/* The agent already renders a branded, self-contained document — the
                same HTML the PNG is made from and the same thing Khine sees on
                WhatsApp. Showing it verbatim beats re-parsing it into something
                that looks different. Sandboxed: our own content, but it is still
                stored markup, so no scripts, no forms, no same-origin. */}
            {tab === 'report' && report?.html && (
              <iframe
                title="Report"
                sandbox=""
                srcDoc={report.html}
                className="w-full h-full border-0 bg-white"
              />
            )}
            {tab === 'report' && report && !report.html && (
              <pre className="p-6 text-sm whitespace-pre-wrap text-slate-700">{report.markdown}</pre>
            )}

            {tab === 'items' && report && (
              <div className="h-full overflow-y-auto p-5">
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(
                    report.insights.reduce<Record<string, number>>((acc, i) => {
                      acc[i.kind] = (acc[i.kind] ?? 0) + 1; return acc;
                    }, {})
                  ).map(([k, n]) => (
                    <Badge key={k} className={KIND_TONE[k] ?? KIND_TONE.note}>{n} {k}</Badge>
                  ))}
                </div>
                {Object.entries(
                  report.insights.reduce<Record<string, Insight[]>>((acc, i) => {
                    const k = i.section ?? 'Other';
                    (acc[k] ??= []).push(i); return acc;
                  }, {})
                ).map(([section, rows]) => (
                  <div key={section} className="mb-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{section}</h3>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="w-full text-sm">
                        <tbody className="divide-y divide-slate-100">
                          {rows.map((i, n) => (
                            <tr key={n} className="align-top hover:bg-slate-50">
                              <td className="px-3 py-2 w-20">
                                <Badge className={`${KIND_TONE[i.kind] ?? KIND_TONE.note} text-[10px]`}>{i.kind}</Badge>
                              </td>
                              <td className="px-3 py-2 font-medium text-slate-900">{i.title}</td>
                              <td className="px-3 py-2 text-slate-600">
                                {/* the agent writes table rows as pipe-separated cells; show them as cells */}
                                {(i.detail ?? '').split('|').filter(c => c.trim() && c.trim() !== '—').map((c, ci) => (
                                  <span key={ci} className="block leading-snug">{c.trim()}</span>
                                ))}
                              </td>
                              <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">{i.owner ?? ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default AutomationView;
